import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import kidImage from '../assets/about/kid.avif';
import siblingsImage from '../assets/about/siblings.avif';
import { useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import SEO from '../components/SEO';
import { generatePageSEO, generateBreadcrumbData } from '../utils/seoUtils';

const About = () => {
  const { announce } = useAccessibility();
  const seoData = generatePageSEO('about');
  
  const breadcrumbData = generateBreadcrumbData([
    { name: 'Home', url: '/' },
    { name: 'About', url: '/about' }
  ]);

  useEffect(() => {
    // Announce page load to screen readers
    announce("About Begard Gear page loaded", "polite");
  }, [announce]);

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        type={seoData.type}
        structuredData={[breadcrumbData]}
      />
      
    <Box 
      sx={{ py: 6, backgroundColor: 'background.default' }}
      role="main"
      aria-labelledby="about-page-title"
    >
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h1" 
          align="center" 
          gutterBottom
          sx={{ fontWeight: 'bold', mb: 6, color: 'text.primary' }}
          id="about-page-title"
        >
          About Begard Gear
        </Typography>

        {/* Our story Section */}
        <Paper 
          elevation={3} 
          sx={{ p: 4, mb: 6, borderRadius: 2, backgroundColor: 'background.paper' }}
          component="section"
          aria-labelledby="our-story-heading"
        >
           <Grid container spacing={4} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
             <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src={siblingsImage}
                alt="Two siblings in outdoor gear exploring the Minnesota Northwoods wilderness"
                sx={{ 
                  width: '100%', 
                  borderRadius: 2, 
                  boxShadow: 3, 
                  display: 'block' // Ensure block display to prevent extra space below image
                }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ color: 'primary.main', fontWeight: 'medium' }}
                id="our-story-heading"
              >
              OUR STORY
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary' }}>
                Siblings growing up in the great wilderness, we were fortunate to be raised in the beautiful setting of Minnesota's Northwoods. As children, we adventured through a county with more public land than private, enjoying hiking, camping, swimming, and countless other outdoor activities. This connection to nature remains strong with us today, and we hope to pass it on to the next generation. While we didn't always have the most state-of-the-art outdoor gear, we embraced every adventure nature offered.
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary' }}>
                As adults, life led us to different regions of the country, with one of us remaining in Minnesota while the other relocated to California. As an outdoors family, our perspective on the outdoors shifted dramatically when my son developed a severe allergy to insect stings. Suddenly, safety and prevention became the top priority for all our outdoor activities. Preparedness became essential. After researching online, we discovered a significant gap: few resources were dedicated to insect sting information, and even fewer focused on outdoor gear and clothing specifically designed for everyday prevention of insect stings. My brother and I decided to turn this challenge into an opportunity, launching this business to provide carefully selected outdoor gear and apparel that follows recommendations for reducing the risk of stings.
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary' }}>
                Our mission combines our lifelong love of the outdoors with practical solutions for safer adventures—because everyone deserves to enjoy nature with confidence.
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Our Mission Section */}
        <Paper 
          elevation={3} 
          sx={{ p: 4, mb: 6, borderRadius: 2 }}
          component="section"
          aria-labelledby="our-mission-heading"
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ color: 'primary.main', fontWeight: 'medium' }}
                id="our-mission-heading"
              >
                Enjoying the outdoors while living with an allergy
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary' }}>
              I have spent a great deal of my professional life and my education dedicated to the outdoors, both in the private and public sectors.  I carry the belief that the outdoors are for everyone.  Sometimes it just takes more preparation.
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary' }}>
              Living with an insect sting allergy means being prepared.  Keeping your medications close at hand, arming yourself with knowledge, utilizing apparel and gear that will hopefully offer the best options for your situation.  Every situation has risk but we can find more peace of mind and enjoyment if we can mitigate that risk as best as we can.  My hope is that everyone can enjoy, relax, and play in the outdoors. 
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src={kidImage}
                alt="Child wearing protective outdoor gear designed to prevent insect stings while enjoying nature"
                sx={{ 
                  width: '100%', 
                  borderRadius: 2, 
                  boxShadow: 3, 
                  display: 'block' // Ensure block display to prevent extra space below image
                }} 
              />
            </Grid>
          </Grid>
        </Paper>

        

      </Container>
    </Box>
    </>
  );
};

export default About; 